'use client'

import Pill from '@/components/ui/Pill'
import AppIcon from '@/components/content/app/AppIcon'
import Menu from '@/components/ui/Menu'
import Breadcrumb from '@/components/content/sidebar/Breadcrumb'
import { formatDeal, formatEligibility } from '@/components/content/deals/helpers'
import { getBrandColors } from '@/lib/utils'
import { checkAppBranding } from '@/lib/formatting'
import { useDealContext } from '@/context/DealContext'
import type { MenuItem } from '@/helpers/menus'
import type { App, Deal } from '@/payload-types'

type DealSidebarProps = {
  deal: Deal
  link?: string
  menu: MenuItem[]
}

export default function DealSidebar({ deal, link, menu }: DealSidebarProps) {
  const { handleClick } = useDealContext()

  const formattedDeal = formatDeal(deal)
  const formattedEligibility = formatEligibility(deal.eligibility)
  const app = deal.app as App

  const { appIcon, appGlyph } = checkAppBranding(app)
  const { backgroundColor } = getBrandColors(app)

  return (
    <div className="flex w-full flex-row items-center gap-3.5 md:flex-col md:items-start md:p-4">
      <div className="flex w-full min-w-0 flex-1 items-center gap-3.5">
        {(appIcon || appGlyph) && (
          <div className="h-8 w-8 shrink-0 md:h-14 md:w-14">
            <AppIcon
              glyph={appGlyph}
              icon={appIcon}
              name={app.name}
              slug={app.slug}
              background={backgroundColor}
            />
          </div>
        )}
        <div className="hidden flex-col gap-1 leading-none md:mt-1 md:flex md:w-[calc(100%-70px)]">
          <span className="truncate font-semibold text-gray-950 dark:text-white">
            {formattedDeal.shortname}
          </span>
          <span className="truncate text-13 text-gray-600 dark:text-zinc-500">
            For {formattedEligibility.label}
            {formattedEligibility.only ? ' only' : null}
          </span>
        </div>
        <Breadcrumb placeholder="Deal" breakpoint="md" />
      </div>
      <div className="flex shrink-0 gap-2.5 md:w-full">
        {link && (
          <div className="flex w-full">
            <Pill style="primary" className="md:w-full md:justify-center" onClick={handleClick}>
              Claim
            </Pill>
          </div>
        )}
        <Menu items={menu} align="right" subAlign="down" />
      </div>
    </div>
  )
}
